import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getApiUrl } from '../../utils/apiConfig';

const Page = styled.div`
  min-height: 100vh;
  padding-top: clamp(90px, 8vw, 120px);
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.06);
  padding: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BookButton = styled.button`
  width: 100%;
  padding: 1rem 1.25rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  border: 0;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease;
  box-shadow: 0 8px 25px rgba(46,204,113,.35);
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 35px rgba(46,204,113,.5); }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Badge = styled.span`
  display: inline-block;
  margin-left: .5rem;
  padding: .2rem .6rem;
  border-radius: 999px;
  font-size: .8rem;
  background: ${props => props.bg || '#d1fae5'};
  color: ${props => props.color || '#065f46'};
`;

export default function DynamicCourse() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', childrenCount: 1, childrenNames: [''] });
  const [submitting, setSubmitting] = useState(false);

  const prefillBook = searchParams.get('buchen') === '1';

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(getApiUrl(`/public/courses/${encodeURIComponent(slug)}`));
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Kurs konnte nicht geladen werden');
        if (alive) setCourse(data.data);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!loading && prefillBook) {
      const el = document.getElementById('booking-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, prefillBook]);

  const priceDisplay = useMemo(() => {
    if (!course) return '';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: course.currency || 'EUR' }).format(course.price || 0);
    } catch {
      return `${course.price} ${course.currency || 'EUR'}`;
    }
  }, [course]);

  useEffect(() => {
    // Kinder-Namensfelder an Anzahl angleichen
    setForm(v => {
      const count = Math.max(1, parseInt(v.childrenCount || 1, 10));
      let names = v.childrenNames.slice(0, count);
      while (names.length < count) names.push('');
      return { ...v, childrenCount: count, childrenNames: names };
    });
  }, [form.childrenCount]);

  async function handleBook(e) {
    e.preventDefault();
    if (!course) return;

    // Validierung wie QuickCheckout: alle Kindernamen müssen vorhanden sein
    const missing = form.childrenNames.findIndex(n => !String(n || '').trim());
    if (missing !== -1) { setError('Bitte geben Sie alle Kindernamen an.'); return; }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(getApiUrl(`/public/courses/${course.id}/create-checkout`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          children: form.childrenNames.map(n => ({ name: n })),
          children_count: form.childrenCount
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Checkout konnte nicht erstellt werden');

      if (data.data?.checkout_url) {
        window.location.assign(data.data.checkout_url);
        return;
      }

      if (data.data?.session_id) {
        window.location.assign(`/success?session_id=${encodeURIComponent(data.data.session_id)}`);
        return;
      }

      throw new Error('Unerwartete Antwort vom Server.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Page>
      <Helmet>
        <title>{course ? `${course.title} – Kursdetails` : 'Kursdetails'}</title>
        <meta name="description" content={course?.description || 'Kursdetails und Buchung'} />
      </Helmet>

      <Container>
        {loading && <div>Wird geladen…</div>}
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        {!loading && course && (
          <>
            <Header>
              <Title>
                {course.title}
                {course.is_full && <Badge bg="#fee2e2" color="#991b1b">Ausgebucht</Badge>}
                {course.category && <Badge>{course.category}</Badge>}
              </Title>
              <Subtitle>{course.schedule_text || ''}</Subtitle>
            </Header>

            <Grid>
              <Card>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Über den Kurs</h3>
                <p style={{ color: '#4b5563', lineHeight: 1.7, marginTop: '.5rem' }}>{course.description || 'Keine Beschreibung vorhanden.'}</p>
                <div style={{ marginTop: '1rem', color: '#374151', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '.5rem' }}>
                  <div style={{ background:'#f9fafb', padding: '.75rem 1rem', borderRadius: 10 }}>
                    <div style={{ fontSize: '.8rem', color: '#6b7280' }}>Preis</div>
                    <div style={{ fontWeight: 700 }}>{priceDisplay}</div>
                  </div>
                  <div style={{ background:'#f9fafb', padding: '.75rem 1rem', borderRadius: 10 }}>
                    <div style={{ fontSize: '.8rem', color: '#6b7280' }}>Verfügbare Plätze</div>
                    <div style={{ fontWeight: 700 }}>{course.available_spots ?? '-'}</div>
                  </div>
                </div>
              </Card>

              <Card id="booking-form">
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Jetzt buchen</h3>
                <p style={{ color: '#6b7280', marginTop: '.25rem' }}>Sichere Zahlung über Stripe</p>
                <form onSubmit={handleBook}>
                  <div style={{ display: 'grid', gap: '.75rem' }}>
                    <input required placeholder="Ihr Name" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} style={inputStyle} />
                    <input required type="email" placeholder="E-Mail" value={form.email} onChange={e => setForm(v => ({ ...v, email: e.target.value }))} style={inputStyle} />
                    <input placeholder="Telefon (optional)" value={form.phone} onChange={e => setForm(v => ({ ...v, phone: e.target.value }))} style={inputStyle} />
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: '.5rem' }}>
                      <input type="number" min={1} step={1} max={course.available_spots || 1} value={form.childrenCount}
                        onChange={e => setForm(v => ({ ...v, childrenCount: Math.max(1, Math.min(course.available_spots || 1, parseInt(e.target.value || '1', 10))) }))}
                        style={inputStyle} placeholder="Anzahl der Kinder" />
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', color:'#374151', fontWeight:700, background:'#f9fafb', borderRadius:10 }}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: course.currency || 'EUR' }).format((course.price || 0) * (form.childrenCount || 1))} gesamt
                      </div>
                    </div>
                    <div style={{ display:'grid', gap: '.5rem' }}>
                      {Array.from({ length: form.childrenCount }).map((_, idx) => (
                        <input key={idx} required placeholder={`Name des Kindes ${idx+1}`} value={form.childrenNames[idx] || ''}
                          onChange={e => setForm(v => { const names = v.childrenNames.slice(); names[idx] = e.target.value; return { ...v, childrenNames: names }; })}
                          style={inputStyle} />
                      ))}
                    </div>
                    {error && <div style={{ color: '#b91c1c', fontSize: '.9rem' }}>{error}</div>}
                    <BookButton as="button" type="submit" disabled={submitting || (course.available_spots || 0) <= 0} style={{ opacity: (submitting || (course.available_spots || 0) <= 0) ? .6 : 1 }}>
                      {(course.available_spots || 0) <= 0 ? 'Ausgebucht' : (submitting ? 'Wird verarbeitet…' : 'Jetzt sicher bezahlen')}
                    </BookButton>
                  </div>
                </form>
              </Card>
            </Grid>
          </>
        )}
      </Container>
    </Page>
  );
}

const inputStyle = {
  width: '100%',
  padding: '.85rem 1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '10px',
  outline: 'none'
};
